import {
  PHASE_CRITICAL_MS,
  PHASE_TIME_LIMIT_MS,
  PHASE_WARN_MS,
} from "@poppycock/shared";
import { useEffect, useState } from "react";

export type UrgencyLevel = "none" | "warn" | "critical";

export interface PhaseCountdown {
  /** Milliseconds remaining (0 when expired / no timer). */
  remainingMs: number;
  /** Total duration for this phase (for progress bar). */
  totalMs: number;
  /** Formatted mm:ss */
  label: string;
  urgency: UrgencyLevel;
  /** True once the deadline has passed. */
  expired: boolean;
  /** True when a phaseEndsAt is active. */
  active: boolean;
}

function formatMs(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function compute(endsAt: number | undefined | null): PhaseCountdown {
  if (!endsAt || !Number.isFinite(endsAt)) {
    return {
      remainingMs: 0,
      totalMs: PHASE_TIME_LIMIT_MS,
      label: "—",
      urgency: "none",
      expired: false,
      active: false,
    };
  }
  const remainingMs = Math.max(0, endsAt - Date.now());
  const urgency: UrgencyLevel =
    remainingMs <= 0
      ? "critical"
      : remainingMs <= PHASE_CRITICAL_MS
        ? "critical"
        : remainingMs <= PHASE_WARN_MS
          ? "warn"
          : "none";
  return {
    remainingMs,
    totalMs: PHASE_TIME_LIMIT_MS,
    label: formatMs(remainingMs),
    urgency,
    expired: remainingMs <= 0,
    active: true,
  };
}

/** Live countdown from a server epoch deadline. */
export function usePhaseCountdown(
  phaseEndsAt: number | undefined | null,
): PhaseCountdown {
  const [tick, setTick] = useState(() => compute(phaseEndsAt));

  useEffect(() => {
    setTick(compute(phaseEndsAt));
    if (!phaseEndsAt) return;

    const id = window.setInterval(() => {
      setTick(compute(phaseEndsAt));
    }, 200);
    return () => window.clearInterval(id);
  }, [phaseEndsAt]);

  return tick;
}
